import { useMemo } from 'react';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';

import toFieldDefs from '../schema/toFieldDefs';
import { CONTENT_WIDGETS, type FieldDef, type FormSchema } from '../types';

import { defaultAutocompleteRegistry, type AutocompleteRegistry } from './autocompleteRegistry';
import widgetRegistry from './widgetRegistry';

interface FormRendererProps<T extends FieldValues = FieldValues> {
  schema: FormSchema;
  control: Control<T>;
  // Prefix for react-hook-form field names, e.g. 'form_data'.
  namespace?: string;
  registry?: AutocompleteRegistry;
  disabled?: boolean;
}

const FormRenderer = <T extends FieldValues = FieldValues>({
  schema,
  control,
  namespace,
  registry = defaultAutocompleteRegistry,
  disabled
}: FormRendererProps<T>) => {
  const fields = useMemo(() => toFieldDefs(schema), [schema]);

  const fieldName = (field: FieldDef) =>
    (namespace ? `${namespace}.${field.key}` : field.key) as Path<T>;

  return (
    <div className="flex flex-col gap-4" data-testid="form-renderer">
      {fields.map((field) => {
        const Widget = widgetRegistry[field.widget];

        // Content widgets carry no answer, so they render directly instead
        // of through a Controller-wired form field.
        if (CONTENT_WIDGETS.includes(field.widget)) {
          return (
            <Widget
              key={field.key}
              field={field}
              value={null}
              onChange={() => undefined}
              autocompleteRegistry={registry}
            />
          );
        }

        return (
          <Controller
            key={field.key}
            name={fieldName(field)}
            control={control}
            render={({ field: rhfField, fieldState }) => (
              <Widget
                field={field}
                value={rhfField.value}
                onChange={rhfField.onChange}
                error={fieldState.error?.message}
                disabled={disabled}
                autocompleteRegistry={registry}
              />
            )}
          />
        );
      })}
    </div>
  );
};

export default FormRenderer;
